import { endpoints } from "../../configs/Settings";
import { useAuth } from "../../utils/useAuth";
import { useAxios } from "../../utils/useAxios";
import { setApplicationAccessToken } from "src-next/shared/api/utils/tokenStorage";

/**
 * @description
 * Gera um novo token de aplicação "padrão" (sem escopo customizado) e persiste via tokenStorage (sessionStorage).
 *
 * Este fluxo é utilizado em partes internas do sistema que já dependem de um
 * application token compartilhado. Mantemos este comportamento para garantir
 * compatibilidade com código legado.
 *
 * @returns {Promise<string | undefined>} Token no formato 'Bearer {token}' ou undefined em caso de falha
 */
export async function OAuthApplication() {
  const url = endpoints.token_api;
  const data = "grant_type=client_credentials";
  const headers = {
    Accept: "*/*",
    Authorization: await useAuth.getBasicToken(),
    "Content-Type": "application/x-www-form-urlencoded",
  };
  const response = await useAxios
    .post(url, data, {
      headers,
      timeout: 20000,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      // Mantemos o console.error aqui para facilitar diagnóstico em ambiente externo.
      console.error("OAuthApplication error", error);
    });

  if (response?.access_token) {
    setApplicationAccessToken(response.access_token);
    return `Bearer ${response.access_token}`;
  }

  return undefined;
}

/**
 * @description
 * Estrutura simplificada da resposta de token externo vinda do WSO2.
 *
 * - accessToken: token puro (sem Bearer)
 * - expiresIn: tempo de vida em segundos informado pelo WSO2
 */
interface ExternalTokenResponse {
  accessToken: string;
  expiresIn?: number;
}

/**
 * @description
 * Solicita um novo token de aplicação exclusivo para contexto externo,
 * utilizando um escopo específico no WSO2.
 *
 * A ideia é isolar o token externo (por exemplo, usado em iframes públicos)
 * dos demais tokens de aplicação da plataforma, evitando que um invalide o outro.
 *
 * @param scopeId Identificador simples usado para compor o escopo (ex.: random id)
 * @returns {Promise<ExternalTokenResponse | undefined>} Token puro e tempo de expiração
 */
async function requestExternalApplicationToken(
  scopeId: string,
): Promise<ExternalTokenResponse | undefined> {
  // Monta o escopo no formato solicitado: external{random-id}
  const scope = `external${scopeId}`;
  const url = `${endpoints.token_api}?scope=${encodeURIComponent(scope)}`;
  const data = "grant_type=client_credentials";

  const headers = {
    Accept: "*/*",
    Authorization: await useAuth.getBasicToken(),
    "Content-Type": "application/x-www-form-urlencoded",
  };

  const response: any = await useAxios
    .post(url, data, {
      headers,
      timeout: 20000,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      // Esta função é usada em contexto externo; logamos para facilitar análise de erros.
      console.error("requestExternalApplicationToken error", error);
    });

  if (response?.access_token) {
    const expiresIn =
      typeof response.expires_in === "number" ? response.expires_in : undefined;

    return {
      accessToken: response.access_token as string,
      // o back-end informa o tempo em segundos; guardamos para calcular o vencimento
      expiresIn,
    };
  }

  return undefined;
}

/**
 * @description
 * Obtém um token de autenticação exclusivo para contextos externos (ex.: páginas
 * públicas embarcadas via iframe), reutilizando sempre o mesmo token.
 *
 * Regras:
 * - Nunca usa o token do usuário.
 * - Sempre gera ou reutiliza um token de aplicação com escopo external{random-id}.
 * - Salva o token e o identificador de escopo no localStorage para evitar
 *   múltiplas chamadas desnecessárias ao endpoint de token da WSO2.
 *
 * @returns {Promise<string | undefined>} Token no formato 'Bearer {token}' ou undefined em caso de falha
 */
export async function getExternalAuthToken(): Promise<string | undefined> {
  try {
    // 1. Tenta obter token externo já salvo
    const storedExternalToken = localStorage.getItem(
      "external_application_token",
    );
    const storedExternalTokenExpiresAt = localStorage.getItem(
      "external_application_token_expires_at",
    );

    // Quando existir token e data de expiração, validamos antes de reutilizar
    if (storedExternalToken && storedExternalTokenExpiresAt) {
      const expiresAt = Number(storedExternalTokenExpiresAt);
      const now = Date.now();

      // Margem de segurança de 30 segundos para evitar usar token no limite
      const safetyMarginMs = 30 * 1000;

      if (!Number.isNaN(expiresAt) && now < expiresAt - safetyMarginMs) {
        return `Bearer ${storedExternalToken}`;
      }

      // Se estiver expirado (ou com data inválida), limpamos do storage
      localStorage.removeItem("external_application_token");
      localStorage.removeItem("external_application_token_expires_at");
    } else if (storedExternalToken && !storedExternalTokenExpiresAt) {
      // Cenário legado: havia token salvo sem expiração.
      // Mantemos o comportamento antigo e reutilizamos o token.
      return `Bearer ${storedExternalToken}`;
    }

    // 2. Tenta reutilizar o mesmo identificador de escopo, se já existir
    let scopeId = localStorage.getItem("external_application_scope_id");
    if (!scopeId) {
      // Gera um identificador simples e estável para compor o escopo externo.
      // Este valor será reutilizado em chamadas futuras.
      scopeId = Math.random().toString(36).slice(2, 10);
      localStorage.setItem("external_application_scope_id", scopeId);
    }

    // 3. Solicita um novo access_token exclusivo para o escopo externo
    const externalTokenResponse = await requestExternalApplicationToken(scopeId);
    if (!externalTokenResponse?.accessToken) {
      return undefined;
    }

    // 4. Calcula e salva o instante de expiração com base no expires_in
    const now = Date.now();
    if (externalTokenResponse.expiresIn && externalTokenResponse.expiresIn > 0) {
      const expiresAt = now + externalTokenResponse.expiresIn * 1000;
      localStorage.setItem(
        "external_application_token_expires_at",
        String(expiresAt),
      );
    } else {
      // Se o back-end não informar expires_in, garantimos que não fique lixo antigo
      localStorage.removeItem("external_application_token_expires_at");
    }

    // 5. Salva o token externo para reutilização
    localStorage.setItem(
      "external_application_token",
      externalTokenResponse.accessToken,
    );

    return `Bearer ${externalTokenResponse.accessToken}`;
  } catch (error) {
    console.error("getExternalAuthToken error", error);
    return undefined;
  }
}

/**
 * @description
 * Obtém um token de autenticação para chamadas FHIR em geral, priorizando:
 *
 * 1. Token de usuário (quando o usuário está autenticado na aplicação interna).
 * 2. Token externo de aplicação (quando não há usuário autenticado),
 *    usando sempre o mesmo token com escopo external{random-id}.
 *
 * Isso evita que o contexto público (ex.: PractitionerCards em iframe) gere
 * continuamente novos tokens de aplicação que possam invalidar tokens usados
 * pela parte interna do sistema.
 *
 * @returns {Promise<string>} Token no formato 'Bearer {token}'
 * @throws {Error} Se não conseguir obter nenhum token
 */
export async function getAuthToken(): Promise<string> {
  // 1. Tenta obter token de usuário (prioridade máxima)
  const userToken = await useAuth.getUserToken();
  if (userToken) {
    return userToken;
  }

  // 2. Quando não há usuário autenticado, usa sempre o token externo
  //    com escopo específico para evitar interferência com outros tokens.
  const externalToken = await getExternalAuthToken();
  if (!externalToken) {
    throw new Error("Não foi possível obter token de autenticação externo");
  }

  return externalToken;
}
